import styled, { css } from "styled-components";
import React, { forwardRef } from "react";

import { Body1, Body2, Body3 } from "--components/typography";

export interface TimelineProps {
    lineWidth?: number;
    lineColor?: string;
    backgroundColor?: string;
    isCentered?: boolean;
}
export const Timeline = styled.div<TimelineProps>`
    --line-width: ${props => props.lineWidth ?? 2}px;
    --line-color: ${props => props.lineColor ?? props.theme.palette.ui.border};
    --background-color: ${props => props.backgroundColor ?? props.theme.palette.background};

    align-content: start;
    align-items: center;
    display: grid;
    padding: ${props => props.theme.spacing(1, 0)};
    gap: ${props => props.theme.spacing(1.5, 1)};
    grid-template-columns:
        [main-start] ${props => props.isCentered ? "1fr" : "max-content"}
        [line-start] min-content
        [line-end] 1fr
        [main-end];
    position: relative;

    ::before {
        background-color: var(--line-color);
        border-radius: calc(var(--line-width) / 2);
        content: "";
        grid-column: line-start / line-end;
        height: 100%;
        left: 50%;
        position: absolute;
        top: 0;
        transform: translateX(-50%);
        width: var(--line-width);
    }
`;
const StyledTimelineHeader = styled(Body3)`
    background-color: ${props => props.theme.palette.grey.background};
    border-radius: 9999px;
    border: 1px solid ${props => props.theme.palette.ui.border};
    color: ${props => props.theme.palette.text.disabled};
    display: inline-block;
    font-weight: ${props => props.theme.typography.bold};
    margin-top: ${props => props.theme.spacing(0.5)};
    outline: 2px solid var(--background-color);
    padding: ${props => props.theme.spacing(0.25, 1)};
    text-transform: uppercase;
    transform: translateX(-50%);
    white-space: nowrap;
`;
const TimelineHeaderWrapper = styled.div`
    grid-column: line-start / line-end;
    left: 50%;
    position: relative;
    width: 0;
`;
export const TimelineHeader = forwardRef<
    HTMLParagraphElement,
    React.PropsWithChildren<unknown>
>((props, ref) => {
    return (
        <TimelineHeaderWrapper>
            <StyledTimelineHeader ref={ref}>
                {props.children}
            </StyledTimelineHeader>
        </TimelineHeaderWrapper>
    );
});
TimelineHeader.displayName = TimelineHeader.name;

const StyledTimelineEntry = styled.div`
    display: contents;
`;
const TimelineEntryIndicatorWrapper = styled.div<{ indicatorSize: number }>`
    align-items: center;
    display: flex;
    height: ${props => props.indicatorSize}px;
    justify-content: center;
    z-index: 1;
`;
const TimelineEntryIndicator = styled.div<{ color?: string; size?: number; }>`
    --size: ${props => props.size ?? 12}px;
    background-color: ${props => props.color ?? props.theme.palette.primary.main};
    border-radius: 50%;
    grid-column: line-start / line-end;
    justify-self: center;
    min-height: var(--size);
    min-width: var(--size);
    outline: 2px solid var(--background-color);

    :not(:empty) {
        padding: ${props => props.theme.spacing(0.5)};
    }
`;
const TimelineEntryLabel = styled.div`
    grid-column: main-start / line-start;
`;
const TimelineEntryLabelText = styled(Body3)`
    text-align: end;
`;
const TimelineEntryContent = styled.div<{ inline: boolean }>`
    grid-column: line-end / main-end;
    ${props => !props.inline && css`
        margin-top: ${props.theme.spacing(-1)};
    `}
`;
interface TimelineEntryProps {
    title?: React.ReactNode;
    label?: React.ReactNode;
    children?: React.ReactNode;
    indicatorSize?: number;
    indicatorColor?: string;
    indicatorIcon?: React.ReactNode;
}
export const TimelineEntry = (props: TimelineEntryProps) => {
    const { indicatorSize = 12 } = props;

    return (
        <StyledTimelineEntry>
            <TimelineEntryLabel>
                {typeof props.label === "string"
                    ? (
                        <TimelineEntryLabelText>
                            {props.label}
                        </TimelineEntryLabelText>
                    )
                    : props.label}
            </TimelineEntryLabel>
            <TimelineEntryIndicatorWrapper indicatorSize={indicatorSize}>
                <TimelineEntryIndicator
                    color={props.indicatorColor}
                    size={props.indicatorSize}
                >
                    {props.indicatorIcon}
                </TimelineEntryIndicator>
            </TimelineEntryIndicatorWrapper>

            {props.title
                ? (
                    <div>
                        {typeof props.title === "string"
                            ? <Body1>{props.title}</Body1>
                            : props.title}
                    </div>
                )
                : null}

            {props.children && (
                <TimelineEntryContent inline={!props.title}>
                    {typeof props.children === "string"
                        ? <Body2> {props.children} </Body2>
                        : props.children}
                </TimelineEntryContent>
            )}
        </StyledTimelineEntry>
    );
};
